import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { isOwnerWorkspaceTheme, normalizeOwnerWorkspaceTheme } from '../lib/ownerWorkspaceTheme';

// Owner credentials stay separate from the tenant-user session and theme.
const OWNER_TOKEN_KEY = 'owner_token';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OwnerAuthContext = createContext(null);
const cancelledSession = () => Object.assign(new Error('The owner session changed. Please sign in again.'), { code: 'OWNER_SESSION_CHANGED' });
const validOwner = value => value && typeof value === 'object' && !Array.isArray(value) && typeof value.id === 'string' && value.id.trim();

export const useOwnerAuth = () => {
  const context = useContext(OwnerAuthContext);
  if (!context) throw new Error('useOwnerAuth must be used within OwnerAuthProvider');
  return context;
};

export const OwnerAuthProvider = ({ children }) => {
  const [session, setSession] = useState(() => ({ owner: null, token: localStorage.getItem("smmclone-independent:v1:" + (OWNER_TOKEN_KEY)), revision: 0, loading: true }));
  const sessionRef = useRef(session);
  const mounted = useRef(true);
  const requests = useRef(new Set());
  const readVersion = useRef(0);
  const themeVersion = useRef(0);
  const themePending = useRef(null);
  const [theme, setTheme] = useState({ revision: 0, value: 'modern', saving: false });

  const updateSession = useCallback(update => {
    if (!mounted.current) return;
    const next = update(sessionRef.current);
    sessionRef.current = next;
    setSession(next);
  }, []);
  const currentSession = useCallback(scope => mounted.current && sessionRef.current.revision === scope.revision && sessionRef.current.token === scope.token, []);
  const currentOwner = useCallback(scope => currentSession(scope) && sessionRef.current.owner?.id === scope.owner?.id, [currentSession]);
  const abortRequests = useCallback(() => {
    requests.current.forEach(controller => controller.abort());
    requests.current.clear();
    themePending.current = null;
    themeVersion.current += 1;
    readVersion.current += 1;
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; abortRequests(); };
  }, [abortRequests]);

  const logout = useCallback(() => {
    abortRequests();
    localStorage.removeItem("smmclone-independent:v1:" + (OWNER_TOKEN_KEY));
    updateSession(previous => ({ owner: null, token: null, loading: false, revision: previous.revision + 1 }));
    setTheme({ revision: sessionRef.current.revision, value: 'modern', saving: false });
  }, [abortRequests, updateSession]);

  const { owner, token, revision, loading } = session;
  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    if (token) instance.defaults.headers.Authorization = `Bearer ${token}`;
    instance.interceptors.response.use(response => response, error => {
      // A late 401 from another session must not log out the current account.
      if (error.response?.status === 401 && currentSession({ token, revision })) logout();
      return Promise.reject(error);
    });
    return instance;
  }, [token, revision, currentSession, logout]);

  const loadOwner = useCallback(async (scope, controller) => {
    const read = ++readVersion.current;
    const preferenceVersion = themeVersion.current;
    requests.current.add(controller);
    try {
      const response = await axios.get(`${API}/owner/me`, {
        headers: { Authorization: `Bearer ${scope.token}` }, signal: controller.signal,
      });
      if (!currentSession(scope) || controller.signal.aborted || read !== readVersion.current) return null;
      const data = response.data;
      if (!validOwner(data) || (scope.owner && data.id !== scope.owner.id)) throw new Error('The server did not confirm the owner account.');
      updateSession(previous => ({ ...previous, owner: data, loading: false }));
      // A read started before or during a preference save can contain the old
      // value. Keep the confirmed preference without replacing profile data.
      if (preferenceVersion === themeVersion.current && !themePending.current) {
        setTheme({ revision: scope.revision, value: normalizeOwnerWorkspaceTheme(data.workspace_theme), saving: false });
      }
      return data;
    } finally {
      requests.current.delete(controller);
    }
  }, [currentSession, updateSession]);

  useEffect(() => {
    if (!token) {
      updateSession(previous => previous.loading ? { ...previous, loading: false } : previous);
      return undefined;
    }
    const controller = new AbortController();
    const scope = sessionRef.current;
    updateSession(previous => ({ ...previous, loading: true }));
    loadOwner(scope, controller).catch(() => {
      if (currentSession(scope) && !controller.signal.aborted) logout();
    }).finally(() => {
      if (currentSession(scope) && !controller.signal.aborted) updateSession(previous => ({ ...previous, loading: false }));
    });
    return () => controller.abort();
  }, [token, revision, loadOwner, updateSession, currentSession, logout]);

  const login = useCallback(async (email, password) => {
    abortRequests();
    localStorage.removeItem("smmclone-independent:v1:" + (OWNER_TOKEN_KEY));
    updateSession(previous => ({ owner: null, token: null, loading: true, revision: previous.revision + 1 }));
    const scope = sessionRef.current;
    setTheme({ revision: scope.revision, value: 'modern', saving: false });
    const controller = new AbortController();
    requests.current.add(controller);
    try {
      const response = await axios.post(`${API}/owner/login`, { email, password }, { signal: controller.signal });
      if (!currentSession(scope) || controller.signal.aborted) throw cancelledSession();
      const { access_token: accessToken, owner: ownerData } = response.data || {};
      if (typeof accessToken !== 'string' || !accessToken.trim() || !validOwner(ownerData)) throw new Error('The server did not return a valid owner session.');
      localStorage.setItem("smmclone-independent:v1:" + (OWNER_TOKEN_KEY), accessToken);
      updateSession(previous => ({ ...previous, token: accessToken, owner: ownerData, loading: false }));
      setTheme({ revision: scope.revision, value: normalizeOwnerWorkspaceTheme(ownerData.workspace_theme), saving: false });
      return { success: true, owner: ownerData };
    } finally {
      requests.current.delete(controller);
      if (currentSession(scope)) updateSession(previous => ({ ...previous, loading: false }));
    }
  }, [abortRequests, updateSession, currentSession]);

  const signup = useCallback(async payload => {
    const response = await axios.post(`${API}/owner/signup`, payload);
    return response.data;
  }, []);

  const refreshOwner = useCallback(async () => {
    const scope = sessionRef.current;
    if (!scope.token) return null;
    return loadOwner(scope, new AbortController());
  }, [loadOwner]);

  const setWorkspaceTheme = useCallback(async value => {
    const scope = sessionRef.current;
    if (!currentSession({ token, revision }) || !scope.token || !scope.owner || scope.loading) throw cancelledSession();
    if (!isOwnerWorkspaceTheme(value)) throw new Error('Choose Modern or Classic.');
    if (themePending.current) throw new Error('A theme change is already saving.');
    const controller = new AbortController();
    const operation = { scope, controller };
    themePending.current = operation;
    themeVersion.current += 1;
    requests.current.add(controller);
    setTheme(previous => ({ ...previous, revision: scope.revision, saving: true }));
    try {
      const response = await api.patch('/owner/preferences', { workspace_theme: value }, { signal: controller.signal });
      if (!currentOwner(scope) || controller.signal.aborted) throw cancelledSession();
      const confirmed = response.data?.workspace_theme;
      if (!isOwnerWorkspaceTheme(confirmed)) throw new Error('The server did not confirm the saved theme.');
      setTheme({ revision: scope.revision, value: confirmed, saving: false });
      return confirmed;
    } finally {
      requests.current.delete(controller);
      if (themePending.current === operation) {
        themePending.current = null;
        themeVersion.current += 1;
        if (currentOwner(scope)) setTheme(previous => ({ ...previous, saving: false }));
      }
    }
  }, [api, token, revision, currentSession, currentOwner]);

  const workspaceTheme = theme.revision === revision ? theme.value : normalizeOwnerWorkspaceTheme(owner?.workspace_theme);
  const workspaceThemeSaving = theme.revision === revision && theme.saving;
  return (
    <OwnerAuthContext.Provider value={{ owner, token, loading, api, login, signup, logout, refreshOwner,
      workspaceTheme, workspaceThemeSaving, setWorkspaceTheme, ownerSessionRevision: revision }}>
      {children}
    </OwnerAuthContext.Provider>
  );
};

export default OwnerAuthContext;
